import { useCallback, useEffect, useState } from "react";

export const getLocalStorage = () =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- ssr needs this
  globalThis.window == undefined ? undefined : globalThis.localStorage;
export type Codec<T> = {
  encode: (value: T) => string;
  decode: (value: string) => T;
};
export const JsonCodec: Codec<unknown> = {
  decode: JSON.parse,
  encode: JSON.stringify,
};
export const useStateLocalStorage = <State>(
  key: string,
  initialState: State,
  codec: Codec<State> = JsonCodec as Codec<State>,
) => {
  const [state, setState] = useState(initialState);

  useEffect(() => {
    try {
      const storageItem = getLocalStorage()?.getItem(key);
      if (storageItem) {
        setState(codec.decode(storageItem));
      }
    } catch {
      getLocalStorage()?.removeItem(key);
    }
  }, []);

  useEffect(() => {
    getLocalStorage()?.setItem(key, codec.encode(state));
  }, [state]);

  return [state, setState] as const;
};
export const useReducerLocalStorage = <State, Action>(
  key: string,
  reducer: (state: State, action: Action) => State,
  initialState: State,
  codec: Codec<State> = JsonCodec as Codec<State>,
) => {
  const [state, setState] = useStateLocalStorage(key, initialState, codec);

  const dispatch = useCallback(
    (action: Action) => {
      setState((previous) => reducer(previous, action));
    },
    [setState, reducer],
  );

  return [state, dispatch] as const;
};
